/* eslint-disable no-underscore-dangle */
import { RadioGroup } from '@mui/material';
import type { VoidFunctionComponent } from 'react';

import Button from '../../../generic/Button';
import { LabeledPasswordInput, LabeledRadio, LabeledTextInput } from '../../../generic/Forms';
import type { CargoRegistry } from '../../../../../typings/common';

import css from '../../../generic/Forms/Radio.module.less';
import styles from '../Languages.module.less';

type AuthMethod = 'show-token-field' | 'hide-token-field';

type RegistryProps = {
  registry: CargoRegistry;
  onUpdateRegistry: <K extends keyof CargoRegistry>(
    member: K,
    value: CargoRegistry[K],
  ) => void;
  onSetShowTokenField: (value: AuthMethod) => void;
  onRemove: () => void;
};

/**
 * Cargo settings Registry row.
 *
 * Cargo registries support two auth mechanisms:
 * - Bearer token: sent as `Authorization: Bearer <token>` (most common for Cargo registries).
 *   See https://doc.rust-lang.org/cargo/reference/registry-authentication.html
 * - Basic auth (username + password/API key): used by registries like Artifactory.
 */
const Registry: VoidFunctionComponent<RegistryProps> = ({
  registry,
  onUpdateRegistry,
  onSetShowTokenField,
  onRemove,
}) => {
  const selectedRadioButton = registry._showTokenField ? 'show-token-field' : 'hide-token-field';
  return (
    <div className="margin-bottom-sm">
      <RadioGroup
        name={`auth-method-${registry._id}`}
        className={`${css.radios} ${css['radios-horizontal']} ${styles.radioGroup}`}
        value={selectedRadioButton}
        onChange={event => onSetShowTokenField(event.target.value as AuthMethod)}
      >
        <LabeledRadio
          value="show-token-field"
          label="Token"
        />
        <LabeledRadio
          value="hide-token-field"
          label="Username/Password"
        />
      </RadioGroup>
      <div className={styles.fieldsRow}>
        <div className={styles.fieldWrapper}>
          <LabeledTextInput
            label="URL"
            value={registry.url || ''}
            size="small"
            onChange={value => onUpdateRegistry('url', value)}
            placeholder="https://example.jfrog.io/artifactory/api/cargo/repo/index/"
          />
        </div>
        {registry._showTokenField && (
          <div className={styles.fieldWrapper}>
            <LabeledPasswordInput
              label="Token"
              size="small"
              value={registry.hasToken ? '••••••••' : registry.token?.value || ''}
              onChange={value => onUpdateRegistry('token', { __type: 's', value })}
            />
          </div>
        )}
        {!registry._showTokenField && (
          <div className={styles.fieldWrapper}>
            <LabeledTextInput
              label="Username"
              size="small"
              value={registry.username || ''}
              onChange={value => onUpdateRegistry('username', value)}
              placeholder="username"
            />
          </div>
        )}
        {!registry._showTokenField && (
          <div className={styles.fieldWrapper}>
            <LabeledPasswordInput
              label="Password"
              size="small"
              value={registry.hasPassword ? '••••••••' : registry.password?.value || ''}
              onChange={value => onUpdateRegistry('password', { __type: 's', value })}
            />
          </div>
        )}
        <Button
          type="error"
          size="small"
          onClick={onRemove}
        >
          Remove
        </Button>
      </div>
    </div>
  );
};

export default Registry;
